/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import {
  CommonActions, CommunicationEntryPoint, CommunicationListItem, ContactDetailsDialogConfig, contactDetailsDialogRoute, EmailDialogConfig, emailDialogRoute, FormActionPipelines, FormFlowState, MeetingDialogConfig, meetingDialogRoute, SmsDialogConfig, smsDialogRoute, WebsiteDialogConfig, websiteDialogRoute, } from '@gh/builder-shared';
import {
  ActivityRestService, attachmentFromLetter, ActivityDetails, CommunicationType, ContactType, createLinkFromLetter, isDeliveryMethodEmail, } from '@gh/builder-shared-data';
import { textMessageDialogRoute } from '@gh/common-internal-shared';
import {
  ActionFlow, AppRouter, compoundLogicalAction, createFlow, DateTimeTypes, eitherAction, exitAction, exitFlow, FlowStateWithError, FormStateSnapshot, LogicalAction, mapFlowStateAction, mutate, queryFormMode, queryVersion, sideEffectAction, State, streamOptionalValueAction, UserContext, } from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { TranslationService } from '@gh/core-mls';
import {
  DialogService, FormMode, isCreateFormMode, isExistingFormMode, NotificationService, TextMessageDialogConfig, WaitingService, yesAnswer, yesNoConfirmation, } from '@gh/core-ui';
import { createLink } from '@gh/core-util';
import { concatWith$, map$, filter$, first$, switchMap$ } from '@gh/rx/operators';
import { first, isEmpty, noop } from 'lodash';
import { Observable } from 'rxjs';
import { completeSaleDialogRoute } from '../complete-sale-dialog/complete-sale-dialog.component';
import { LeadDetailsBusinessService } from '../../resources/lead-details-business.service';
import {
  docLetterToCommunication,
  getLeadSalesFolderId,
  LeadContactDetails,
  LeadDetailsInit,
  LeadDetailsSaveResponse,
  LeadMasterInfo,
  LeadSalesFolder,
  LeadSalesFolderComplete,
  LeadSalesFolderLetter,
  LeadSalesFolderMeeting,
  mergeLeadCommunicationIntoDetails,
  NONE_APPROVED_QUOTE,
} from '../../resources/lead.model';
import { approvedQuotesDialogRoute } from '../approved-quotes-dialog/approved-quotes-dialog.component';
import { jobDppDetailsDialogRoute } from '../job-dpp-details-dialog/job-dpp-create-dialog.component';
import {
  ComponentLeadDetailsReassignConfig,
  componentReassignLeadDialogRoute,
} from './lead-details-reassign-dialog/lead-details-reassign-dialog.component';
import {
  LeadCommunicationListFilters,
  LeadDetailsState,
  LeadDetailsStateEvent,
  LeadDetailsStateEventType,
  LeadDetailsTabName,
  queryLeadContact,
  queryLeadContactName,
  queryLeadCurrentSalesFolderJobDetails,
  queryLeadDirty,
  queryLeadEmail,
  queryLeadFirstSalesFolder,
  queryLeadGDriveFolderId,
  queryLeadHasErrors,
  queryLeadId,
  queryLeadIsProduceFcl,
  queryLeadIsPromptFcl,
  queryLeadIsSalesFolderSelected,
  queryLeadPhone,
  queryLeadSalesFolder,
  queryLeadSalesFolderDirty,
  queryLeadSalesFolders,
  queryLeadSaveModel,
  querySalesPersonId,
} from './lead-details.state';

export type LeadSaveFlowState = FormFlowState<LeadDetailsState> & { saveResponse: LeadDetailsSaveResponse };
export type ProduceFclFlowState = FlowStateWithError<{ salesFolderId: ID; communication?: CommunicationListItem }>;

export const mergeLeadCommunicationIntoState = (leadState: LeadSaveFlowState, fclState: ProduceFclFlowState) =>
  fclState.communication ?
    ({
      ...leadState,
      saveResponse: mergeLeadCommunicationIntoDetails(leadState.saveResponse, fclState.communication),
    })
    : leadState;

@Injectable()
export class LeadDetailsCommandService {
  constructor(private userContext: UserContext,
              private commonActions: CommonActions,
              private pipelines: FormActionPipelines,
              private businessService: LeadDetailsBusinessService,
              private state: State<LeadDetailsState>,
              private waitingService: WaitingService,
              private dialogService: DialogService,
              private translationService: TranslationService,
              private router: AppRouter,
              private activityService: ActivityRestService,
              private notificationService: NotificationService) {

  }

  createContact(): void {
    this.openContact();
  }

  openContact(openedContact?: LeadContactDetails): void {
    this.dialogService.openWithInput(contactDetailsDialogRoute, <ContactDetailsDialogConfig>{
      contactType: ContactType.Customer,
      contactId: openedContact ? openedContact.contactId : void 0,
      entryPoint: 'lead',
      parentTitle: {
        [FormMode.CreateMode]: 'lbl_lead_create',
        [FormMode.EditMode]: 'lbl_lead_edit',
        [FormMode.ViewMode]: 'lbl_lead_view',
      }[this.state.project(queryFormMode)],
    }).afterClosed().pipe(
      filter$(Boolean))
      .subscribe((contact) => {
        this.state.mutate({ type: LeadDetailsStateEventType.ContactSelect, contact });
      });
  }

  selectContact(contact?: LeadContactDetails): void {
    this.state.mutate({ type: LeadDetailsStateEventType.ContactSelect, contact });
  }

  createDppJob(salesFolderId: ID): void {
    if (!this.ensureSaved()) {
      return;
    }

    const contact = this.state.project(queryLeadContact);
    const sf = this.state.project((s) => queryLeadSalesFolder(s, salesFolderId));

    if (contact && sf) {
      this.dialogService.openWithInput(jobDppDetailsDialogRoute, {
        salesFolderId,
        salesFolderName: sf.name,
        customerName: contact.name,
        address: contact.address,
      }).afterClosed().pipe(
        filter$(Boolean),
        switchMap$(() => this.ensureSalesFolderLoadedAndChoosed(sf)))
        .subscribe(
          (event) => this.state.mutate(event),
          this.notificationService.processAll);
    }
  }

  openRfqManagement(salesFolderId: ID): void {
    this.router.navigateToChild([`/sales/sales-folders/${salesFolderId}/rfq`]);
  }

  load(init: LeadDetailsInit, mode: FormMode): void {
    this.state.mutate({ type: LeadDetailsStateEventType.Load, details: init, mode });
  }

  selectTab(tab: LeadDetailsTabName, salesFolderId?: ID): void {
    const tabSelectedEvent = { type: LeadDetailsStateEventType.TabSelected, tab };
    if (tab === LeadDetailsTabName.SalesFolders && !this.state.project(queryLeadSalesFolders).length) {
      this.state.mutate(tabSelectedEvent);
      return;
    }
    if (tab === LeadDetailsTabName.SalesFolders && !this.state.project(queryLeadIsSalesFolderSelected)) {
      this.state.select(queryLeadFirstSalesFolder).pipe(
        first$(),
        filter$(Boolean))
        .subscribe((sf: any) => this.selectSalesFolder(salesFolderId || getLeadSalesFolderId(sf)));
    } else {
      this.state.mutate(tabSelectedEvent);
    }
  }

  selectSalesFolder(sfId: ID): void {
    const sf = this.state.project(queryLeadSalesFolder, sfId);
    if (sf) {
      this.ensureSalesFolderLoadedAndChoosed(sf).pipe(
        concatWith$([{ type: LeadDetailsStateEventType.TabSelected, tab: LeadDetailsTabName.SalesFolders }]),
        (mutate(this.state)))
        .subscribe({ next: noop, error: this.notificationService.processAll });
    }
  }

  setMasterInfo(masterInfo: FormStateSnapshot<LeadMasterInfo>): void {
    this.state.mutate({ type: LeadDetailsStateEventType.SetMasterInfo, masterInfo });
  }

  salesFolderAdd(): void {
    this.state.mutate({ type: LeadDetailsStateEventType.SalesFolderAdd });
  }

  salesFolderEdit(): void {
    this.state.mutate({ type: LeadDetailsStateEventType.SalesFolderEdit });
  }

  salesFolderReset(): void {
    if (this.state.project(queryLeadSalesFolderDirty)) {
      this.dialogService.confirmation(yesNoConfirmation('msg_reset')).pipe(
        filter$(yesAnswer),
        map$(() => ({ type: LeadDetailsStateEventType.SalesFolderReset })),
        (mutate(this.state)))
        .subscribe();
    } else {
      this.state.mutate({ type: LeadDetailsStateEventType.SalesFolderReset });
    }
  }

  salesFolderChange(folderId: ID): void {
    const formMode = this.state.project(queryFormMode);

    if (isExistingFormMode(formMode)) {
      this.state.select((s) => queryLeadSalesFolder(s, folderId)).pipe(
        first$(),
        filter$(Boolean),
        switchMap$((salesFolder: any) => this.ensureSalesFolderLoadedAndChoosed(salesFolder)),
        (mutate(this.state)))
        .subscribe({ next: noop, error: this.notificationService.processAll });
    } else {
      this.state.mutate({ type: LeadDetailsStateEventType.SalesFolderChange, folderId });
    }
  }

  sendEmail(): void {
    const email = this.state.project(queryLeadEmail);
    const contactId = this.state.project(queryLeadId)!;

    this.dialogService.openWithInput(emailDialogRoute, <EmailDialogConfig>{
      entryPoint: CommunicationEntryPoint.Lead,
      contactId,
      email: {
        email,
        subject: '',
        message: '',
        communicationType: CommunicationType.Email,
        attachments: [],
        contactId,
        isMarkedAsRead: true,
      },
      gFolderId: this.state.project(queryLeadGDriveFolderId),
    }).afterClosed().pipe(
      filter$(Boolean))
      .subscribe((communicationListItem) => {
        this.state.mutate({ type: LeadDetailsStateEventType.CommunicationListItemAdd, communicationListItem });
      });
  }

  sendSms(): void {
    const cellPhone = this.state.project(queryLeadPhone);
    const contactId = this.state.project(queryLeadId);

    this.dialogService.openWithInput(smsDialogRoute, <SmsDialogConfig>{
        entryPoint: CommunicationEntryPoint.Lead,
        contactId,
        sms: {
          cellPhone,
          subject: '',
          message: '',
          communicationType: CommunicationType.Email,
          attachments: [],
          contactId,
          isMarkedAsRead: true,
        },
      })
      .afterClosed().pipe(
      filter$(Boolean))
      .subscribe((communicationListItem) => {
        this.state.mutate({ type: LeadDetailsStateEventType.CommunicationListItemAdd, communicationListItem });
      });
  }

  showEmail(communicationListItem: CommunicationListItem): void {
    const contactId = this.state.project(queryLeadId);
    this.dialogService.openWithInput(emailDialogRoute, <EmailDialogConfig>{
      entryPoint: CommunicationEntryPoint.Lead,
      contactId,
      email: communicationListItem,
      communicationId: communicationListItem.communicationId,
    }).afterClosed().pipe(
      filter$(Boolean))
      .subscribe();
  }

  showSms(communicationListItem: CommunicationListItem): void {
    const contactId = this.state.project(queryLeadId);
    this.dialogService.openWithInput(smsDialogRoute, <SmsDialogConfig>{
      entryPoint: CommunicationEntryPoint.Lead,
      contactId,
      sms: {
        communicationId: communicationListItem.communicationId,
        cellPhone: communicationListItem.cellPhone,
        message: communicationListItem.message,
        isMarkedAsRead: communicationListItem.isMarkedAsRead,
      },
    }).afterClosed().pipe(
      filter$(Boolean))
      .subscribe((item) =>
        this.state.mutate({
          type: LeadDetailsStateEventType.CommunicationListItemUpdate,
          communicationListItem: item }));
  }

  showWebsite(communicationListItem: CommunicationListItem): void {
    this.dialogService.openWithInput(websiteDialogRoute, <WebsiteDialogConfig>{
      entryPoint: CommunicationEntryPoint.Lead,
      website: communicationListItem,
    }).afterClosed().pipe();
  }

  showDoc(communicationListItem: CommunicationListItem): void {
    this.businessService
      .getCommunicationDetails(communicationListItem.communicationId!).pipe(
      this.waitingService.operator())
      .subscribe(
        (fileInfo: CommunicationListItem) => {
          const attachment = first(fileInfo.attachments);
          const link = createLink(attachment!.webViewLink, attachment!.googleFileName);

          this.dialogService.openWithInput(textMessageDialogRoute, <TextMessageDialogConfig<string>>{
            title: this.translationService.get('lbl_generated_doc'),
            html: `${this.translationService.get('lbl_link_to_generated_doc')}:\n${link}`,
            buttons: [
              { title: this.translationService.get('lbl_ok'), value: 'ok', type: 'primary' },
            ],
          });
        },
        this.notificationService.processAll);
  }

  meetingAdd(salesFolderId: ID, callback: (result: ActivityDetails | LeadSalesFolderMeeting) => void): void {
    this.dialogService.openWithInput(meetingDialogRoute, <MeetingDialogConfig>{
      entryPoint: CommunicationEntryPoint.Lead,
      salesFolderId,
      mode: FormMode.CreateMode,
      helpId: 'sales/meetings',
    }).afterClosed().pipe(
      filter$(Boolean))
      .subscribe((meeting: ActivityDetails) => {
        if (meeting.isDone) {
          this.dialogService.confirmation(yesNoConfirmation('msg_activity_marked_as_done')).pipe(
            filter$(yesAnswer))
            .subscribe(() => this.meetingAdd(salesFolderId, callback));
        }
        this.state.mutate({
                            type: LeadDetailsStateEventType.CommunicationListItemAdd,
                            communicationListItem: {...meeting, communicationType: CommunicationType.SalesActivity },
        });
        return callback(meeting);
      });
  }

  meetingOpen(salesFolderId: ID, meeting: LeadSalesFolderMeeting, callback: (result: LeadSalesFolderMeeting) => void): void {
    const isDone = meeting.isDone;
    this.activityService.get(salesFolderId, meeting.id || '').subscribe((meetingData: ActivityDetails) => {
      this.dialogService.openWithInput(meetingDialogRoute, <MeetingDialogConfig>{
        entryPoint: CommunicationEntryPoint.Lead,
        salesFolderId,
        meeting: meetingData,
        mode: !meeting.active ? FormMode.ViewMode : this.state.project(queryFormMode),
        helpId: 'sales/meetings',
      }).afterClosed().pipe(
        filter$(Boolean))
        .subscribe((updatedMeeting: LeadSalesFolderMeeting) => {
          if (updatedMeeting.isDone && !isDone) {
            this.dialogService.confirmation(yesNoConfirmation('msg_activity_marked_as_done')).pipe(
              filter$(yesAnswer))
              .subscribe(() => this.meetingAdd(salesFolderId, callback));
          }
          this.state.mutate({
                              type: LeadDetailsStateEventType.CommunicationListItemUpdate,
                              communicationListItem: {...updatedMeeting, communicationType: CommunicationType.SalesActivity },
                            });
          callback(updatedMeeting);
        });
    });
  }

  meetingDelete(salesFolderId: ID, meeting: LeadSalesFolderMeeting): void {
    this.dialogService.confirmation(yesNoConfirmation(
      'msg_sf_delete_meeting',
      { date: DateTimeTypes.DateTime.format(meeting.startDate) })).pipe(
      filter$(yesAnswer),
      switchMap$(() => this.businessService.deleteMeeting(salesFolderId, meeting).pipe(
        this.waitingService.operator<void>())))
      .subscribe(
        () => {},
        this.notificationService.processAll);
  }

  commitChanges(saveResponse: LeadDetailsSaveResponse): void {
    this.state.mutate({ type: LeadDetailsStateEventType.CommitChanges, saveResponse });
  }

  setCurrentSalesFolder(snapshot: FormStateSnapshot<LeadSalesFolder>, isInit: boolean): void {
    if (isInit) {
      this.state.mutate({ type: LeadDetailsStateEventType.CurrentSalesFolderSetGeneralInit, snapshot });
      return;
    }
    this.state.mutate({ type: LeadDetailsStateEventType.CurrentSalesFolderSetGeneral, snapshot });
  }

  setMemo(memo: string): void {
    this.state.mutate({ type: LeadDetailsStateEventType.CurrentSalesFolderSetMemo, memo });
  }

  completeFlow(): ActionFlow<LeadSaveFlowState> {
    return this.pipelines.complete({
      validateForm: this.commonActions.validateWithEffect(
        () => this.state.project(queryLeadHasErrors),
        () => this.handleErrors()),
      execute: this.saveAction(),
      finish: (state: LeadSaveFlowState) => {
        this.commitChanges(state.saveResponse);
        if (isCreateFormMode(this.state.project(queryFormMode))) {
          this.router.switchTo(['/sales/leads', this.state.project(queryLeadId)]);
        }
      },
    });
  }

  saveAction(): LogicalAction<LeadSaveFlowState> {
    const executeAction = this.commonActions.async((state: LeadSaveFlowState) =>
      this.businessService.save(queryLeadSaveModel(state.value)).pipe(
        map$((saveResponse) => ({ ...state, saveResponse }))));

    return compoundLogicalAction(
      'success',
      'fail',
      {
        'start': eitherAction((state: LeadSaveFlowState) => queryLeadIsProduceFcl(state.value))
          .bindAs('saveAndProduceFcl', 'save'),
        'save': executeAction.bindAs('success', 'error'),
        'saveAndProduceFcl': executeAction.bindAs('produceFcl.whetherToPrompt', 'error'),
        'produceFcl.whetherToPrompt': eitherAction((state: LeadSaveFlowState) => queryLeadIsPromptFcl(state.value))
          .bindAs('produceFcl.confirm', 'success'),
        'produceFcl.confirm': this.commonActions.textMessage(
          this.generateLetterTemplateConfirmation(),
          {
            'yes': 'produceFcl.start',
            'no': 'success',
          },
          { disableClose: true }),
        'produceFcl.start': mapFlowStateAction(
          (state: LeadSaveFlowState) => ({
            salesFolderId: state.saveResponse.currentSalesFolder!.salesFolderId,
          }),
          mergeLeadCommunicationIntoState,
          this.produceFirstContactLetterAction(),
        ).bindAs('success', 'error'),
        'success': exitAction('success'),
        'error': this.commonActions.notifications().bindAs('fail'),
        '*': exitAction('fail'),
      });
  }

  produceFirstContactLetter(salesFolderId: ID): void {
    if (!this.ensureSaved()) {
      return;
    }

    const flow = this.produceFirstContactLetterFlow();

    flow.run({ salesFolderId });
  }

  reassign(): void {
    if (!this.ensureSaved()) {
      return;
    }

    const leadId = this.state.project(queryLeadId);
    const excludedSalesPersonId =  this.state.project(querySalesPersonId);
    this.reassignLeadDialog(leadId, excludedSalesPersonId);
  }

  setCommunicationListFilter(communicationListFilters: LeadCommunicationListFilters): void {
    this.state.mutate({ type: LeadDetailsStateEventType.ChangeCommunicationListFilter, communicationListFilters });
  }

  produceFirstContactLetterFlow(): ActionFlow<ProduceFclFlowState> {
// @ts-expect-error Legacy typing suppression
    return createFlow({
      'start': this.commonActions.textMessage(this.generateLetterTemplateConfirmation(), {
        'yes': 'produceFcl.start',
        'no': 'finish',
        'otherwise': 'finish',
      }),
      'produceFcl.start': this.produceFirstContactLetterAction().bindAs('communication.add', 'error'),
      'communication.add': sideEffectAction(({ communication }) => {
        if (communication) {
          this.state.mutate({
            type: LeadDetailsStateEventType.CommunicationListItemAdd,
            communicationListItem: communication,
          });
        }
      }).bindAs('finish'),
      'error': this.commonActions.notifications().bindAs('finish'),
      '*': exitFlow(),
    });
  }

  produceAuthorityToProceed(salesFolderId: ID): void {
    if (!this.ensureSaved()) {
      return;
    }

    this.businessService.findAllApprovedRfqNumbers(salesFolderId).pipe(
      this.waitingService.operator(),
      switchMap$((quotes) => {
        if (isEmpty(quotes)) {
          // if not approved quotes were found we return undefined
          return [NONE_APPROVED_QUOTE];
        } else {
          return this.dialogService.openWithInput(approvedQuotesDialogRoute, { quotes }).afterClosed();
        }
      }),
      filter$(Boolean),
      // generate authority to proceed for selected quote, if any
      switchMap$((quote: any) => this.businessService.generateAuthorityToProceedLetter(
        salesFolderId,
        quote && quote.quotationId).pipe(
        (this.waitingService.operator()))),
      switchMap$((letter) => {
        if (isDeliveryMethodEmail(letter.deliveryBy)) {
          return this.dialogService.openWithInput(emailDialogRoute, <EmailDialogConfig>{
            entryPoint: CommunicationEntryPoint.Lead,
            contactId: this.state.project(queryLeadId),
            email: {
              contactId: this.state.project(queryLeadId),
              email: this.state.project(queryLeadEmail),
              attachments: [attachmentFromLetter(letter)],
            },
          }).afterClosed();
        } else {
          return this.dialogService.openWithInput(textMessageDialogRoute, <TextMessageDialogConfig<string>>{
            title: this.translationService.get('lbl_link_to_print_authority_to_proceed'),
            html: this.translationService.get('lbl_text_print_authority_document', {
              url: createLinkFromLetter(letter),
            }),
            buttons: [
              { title: this.translationService.get('lbl_ok'), value: 'ok', type: 'primary' },
            ],
          }).afterClosed().pipe(
            // return undefined here in order to distinguish sending of email from any other outcome
            map$(() => docLetterToCommunication(letter, this.userContext.data.fullName, this.state.project(queryLeadId))));
        }
      })).subscribe(
      (communicationListItem?: CommunicationListItem) => {
        if (communicationListItem) {
          this.state.mutate({ type: LeadDetailsStateEventType.CommunicationListItemAdd, communicationListItem });
        }
      },
      this.notificationService.processAll);
  }

  completeSale(salesFolderId: ID): void {
    if (!this.ensureSaved()) {
      return;
    }

    const sf = this.state.project((s) => queryLeadSalesFolder(s, salesFolderId));
    const jobDetails = this.state.project(queryLeadCurrentSalesFolderJobDetails);

    if (sf) {
      this.dialogService.openWithInput(completeSaleDialogRoute, {
        salesFolderId,
        salesFolderName: sf.name,
        customerName: this.state.project(queryLeadContactName),
        ...(jobDetails && jobDetails.isDpp ? {
          dppJobNumber: jobDetails.jobNumber,
          dppBaseDesign: jobDetails.baseDesign,
          dppMemo: jobDetails.memo,
        } : {}),
      }).afterClosed().pipe(
        filter$(Boolean),
        switchMap$(() => this.ensureSalesFolderLoadedAndChoosed(sf)))
        .subscribe(
          (event) => this.state.mutate(event),
          this.notificationService.processAll);
    }
  }

  private generateLetterTemplateConfirmation(): TextMessageDialogConfig<any> {
    return {
      title: this.translationService.get('lbl_first_contact_letter'),
      header: this.translationService.get('msg_contact_letter_produce'),
      text: this.translationService.get('lbl_1st_contact_letter_text'),
      buttons: [
        { title: this.translationService.get('lbl_yes'), value: 'yes', type: 'primary' },
        { title: this.translationService.get('lbl_no'), value: 'no' },
      ],
    };
  }

  private produceFirstContactLetterAction(): LogicalAction<ProduceFclFlowState> {
    const generateLetterConfirmation = (letter: LeadSalesFolderLetter) => <TextMessageDialogConfig<LeadSalesFolderLetter>>({
      title: this.translationService.get('lbl_edit_1st_contact_letter'),
      html: this.translationService.get('lbl_edit_1st_contact_letter_text', { url: createLinkFromLetter(letter) }),
      buttons: [{
        title: this.translationService.get('lbl_confirm'),
        automationId: 'confirm',
        value: letter,
        type: 'primary',
      }],
    });

    const generateLetterReady = (letter: LeadSalesFolderLetter) => <TextMessageDialogConfig<string>>({
      title: this.translationService.get('lbl_print_1st_contact_letter'),
      html: this.translationService.get('lbl_print_1st_contact_letter_text', {
        url: createLinkFromLetter(letter),
      }),
      buttons: [{ title: this.translationService.get('lbl_ok'), value: 'ok', type: 'primary' }],
    });

    return streamOptionalValueAction((state) => {
      const sfId = state.salesFolderId;

      return this.businessService.generateFirstContactLetterTemplate(sfId).pipe(
        this.waitingService.operator(),
        switchMap$((letter) => this.dialogService.openWithInput(
          textMessageDialogRoute,
          generateLetterConfirmation(letter),
          { disableClose: true }).afterClosed()),
        switchMap$((letter) =>
          this.businessService.generateFirstContactLetter({
            contactId: this.state.project(queryLeadId)!,
            salesFolderId: sfId,
            filledTemplate: letter,
          }).pipe(this.waitingService.operator())),
        switchMap$((letter) => {
          if (isDeliveryMethodEmail(letter.deliveryBy)) {
            return this.dialogService.openWithInput(emailDialogRoute, <EmailDialogConfig>{
              entryPoint: CommunicationEntryPoint.Lead,
              contactId: this.state.project(queryLeadId),
              email: {
                contactId: this.state.project(queryLeadId),
                email: this.state.project(queryLeadEmail),
                attachments: [attachmentFromLetter(letter)],
              },
            }).afterClosed();
          } else {
            return this.dialogService.openWithInput(textMessageDialogRoute, generateLetterReady(letter))
              .afterClosed().pipe(
                map$(() => docLetterToCommunication(
                  letter,
                  this.userContext.data.fullName,
                  this.state.project(queryLeadId))));
          }
        }),
        map$((communication) => ({ ...state, communication })));
    });
  }

  private ensureSalesFolderLoadedAndChoosed(sf: LeadSalesFolder): Observable<LeadDetailsStateEvent> {
    return this.loadSalesFolder(sf).pipe(concatWith$([<LeadDetailsStateEvent>{
      type: LeadDetailsStateEventType.SalesFolderChange,
      folderId: getLeadSalesFolderId(sf),
    }]));
  }

  private loadSalesFolder(sf: LeadSalesFolder): Observable<LeadDetailsStateEvent> {
    return this.businessService.getCompleteSalesFolder(getLeadSalesFolderId(sf)).pipe(
      this.waitingService.operator<LeadSalesFolderComplete>(),
      map$((salesFolder) => <LeadDetailsStateEvent>({
        type: LeadDetailsStateEventType.SalesFolderLoad,
        salesFolder,
      })));
  }

  private handleErrors(): void {
    this.state.mutate({ type: LeadDetailsStateEventType.NavigateToInvalid });
  }

  private reassignLeadDialog(leadId?: ID, excludedSalesPersonId?: ID): void {
    const mode = this.state.project(queryFormMode);

    this.dialogService.openWithInput(
      componentReassignLeadDialogRoute,
      <ComponentLeadDetailsReassignConfig>{
        leadId,
        excludedSalesPersonId,
        version: this.state.project(queryVersion),
      }).afterClosed().pipe(
      filter$(Boolean))
      .subscribe((init: LeadDetailsInit) => {
        this.notificationService.success('msg_saved_successfully');
        this.state.mutate({ type: LeadDetailsStateEventType.Load, details: init, mode });
      });
  }

  private ensureSaved(): boolean {
    if (this.state.project(queryLeadDirty)) {
      this.notificationService.error('msg_save_first');
      return false;
    }

    return true;
  }
}
